// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/glyph/ChannelGlyphField.module.css';
import {ChannelGlyphPicker} from '@app/features/channel/glyph/ChannelGlyphPicker';
import {ChannelGlyphView} from '@app/features/channel/glyph/ChannelGlyphView';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {parseChannelGlyph} from '@fluxer/constants/src/ChannelGlyph';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ShapesIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {useId, useState} from 'react';

const CHANNEL_ICON_DESCRIPTOR = msg({
	message: 'Channel icon',
	comment: 'Tooltip and accessible label of the button that opens the channel icon picker.',
});

interface ChannelGlyphFieldProps {
	value: string | null;
	onChange: (value: string | null) => void;
	channelName?: string;
	isVoice?: boolean;
	channelId?: string;
}

/** Square button that shows the current channel glyph and opens the picker. */
export function ChannelGlyphField({value, onChange, channelName, isVoice, channelId}: ChannelGlyphFieldProps) {
	const {i18n} = useLingui();
	const popoutId = useId();
	const [open, setOpen] = useState(false);
	const hasGlyph = parseChannelGlyph(value) != null;
	return (
		<Popout
			uniqueId={`channel-glyph-${popoutId}`}
			position="bottom-start"
			animationType="none"
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			render={({onClose}) => (
				<ChannelGlyphPicker
					value={value}
					onChange={onChange}
					onClose={onClose}
					channelName={channelName}
					isVoice={isVoice}
					channelId={channelId}
				/>
			)}
		>
			<button
				type="button"
				className={clsx(styles.trigger, open && styles.triggerOpen, hasGlyph && styles.triggerFilled)}
				aria-label={i18n._(CHANNEL_ICON_DESCRIPTOR)}
				title={i18n._(CHANNEL_ICON_DESCRIPTOR)}
				aria-haspopup="dialog"
				aria-expanded={open}
				data-flx="channel.glyph-field.trigger"
			>
				{hasGlyph ? (
					<ChannelGlyphView glyph={value} className={styles.glyph} animate />
				) : (
					<ShapesIcon className={styles.placeholder} weight="duotone" />
				)}
			</button>
		</Popout>
	);
}
